import { useEffect, useState } from "react";
import apiFetch from "../../../../../shared/services/apiClient";
import FilialDetailsSummary from "./FilialDetailsSummary";
import styles from "../../styles/addInvoice.module.css";
import { useEsCliente } from "../../../../../shared/hooks/useTenantTipo";
import { useLang } from "../../../../../shared/i18n/useLang";

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        color: "#f87171",
        fontSize: "0.77rem",
        marginTop: 4,
        fontWeight: 500,
      }}
    >
      <i
        className="fa-solid fa-circle-exclamation"
        style={{ fontSize: "0.72rem" }}
      />
      {msg}
    </div>
  );
}

export default function FilialDetailsBody({
  invoiceData,
  updateField,
  errors = {},
}) {
  const [filiales, setFiliales] = useState([]);
  const [loading, setLoading] = useState(true);
  const esCliente = useEsCliente();
  const L = useLang();

  useEffect(() => {
    async function fetchData() {
      try {
        // Usar /filial/ en vez de /propietario/ para mostrar TODAS las filiales
        // aunque no tengan propietario asignado (ej. condominios nuevos o sin residente)
        const res = await apiFetch("/api/residents/filial/");
        const data = await res.json();
        setFiliales(data.results ?? data ?? []);
      } catch {
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Auto-seleccionar primera filial cuando:
  // a) filiales acaban de cargar (primer render)
  // b) invoiceData.filial vuelve a 0 (después de resetFields)
  useEffect(() => {
    if (filiales.length > 0 && invoiceData.filial === 0) {
      const first = filiales[0];
      updateField("filial", first.id);
      updateField("nombre_propietario", first.propietario_nombre || "");
      resolverCuentaCxc(first.id, first.idFilial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filiales, invoiceData.filial]);

  function handleChange(id) {
    const f = filiales.find((f) => f.id === id);
    if (!f) return;
    updateField("filial", f.id);
    updateField("nombre_propietario", f.propietario_nombre || "");
    resolverCuentaCxc(f.id, f.idFilial);
  }

  async function resolverCuentaCxc(filialId, idFilial) {
    try {
      // La cuenta CxC de la filial está en el catálogo bajo "Cuentas por Cobrar"
      // con filial FK = filialId. El endpoint /catalogo/detalle/ la incluye.
      const res  = await apiFetch(`/api/finance/catalogo/detalle/?tipo=ACTIVO`);
      const data = await res.json();
      const cuentas = Array.isArray(data) ? data : (data.results ?? []);
      // La cuenta de la filial tiene el nombre igual al idFilial de la filial
      const cuenta = cuentas.find((c) => c.nombre === idFilial);
      updateField("cuenta_cxc", cuenta ? cuenta.id : null);
    } catch (_) {
      updateField("cuenta_cxc", null);
    }
  }

  return (
    <div className={styles["invoice-details-body"]}>
      <label>
        {esCliente ? L("Cliente", "Client") : L("Filial", "Unit")} <span style={{ color: "#f87171" }}>*</span>
      </label>

      {loading ? (
        <div
          style={{ color: "var(--text-secondary)", fontSize: "0.85rem", padding: "8px 0" }}
        >
          {esCliente ? L("Cargando clientes…", "Loading clients…") : L("Cargando filiales…", "Loading units…")}
        </div>
      ) : filiales.length === 0 ? (
        <div
          style={{
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 6,
            padding: "10px 14px",
            color: "#f87171",
            fontSize: "0.83rem",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <i className="fa-solid fa-triangle-exclamation" />
          {esCliente
            ? L("No hay clientes registrados. Agregá un cliente primero.", "No clients registered. Add a client first.")
            : L("No hay filiales registradas. Agregá un residente primero.", "No units registered. Add a resident first.")}
        </div>
      ) : (
        <>
          <select
            className="form-select"
            value={invoiceData.filial}
            onChange={(e) => handleChange(Number(e.target.value))}
            style={errors.filial ? { borderColor: "#ef4444" } : undefined}
          >
            {filiales.map((f) => (
              <option key={f.id} value={f.id}>
                {f.idFilial}
              </option>
            ))}
          </select>
          <FieldError msg={errors.filial} />
        </>
      )}

      {/* En modo cliente el desplegable ya muestra el nombre; el campo es redundante */}
      {!esCliente && (
        <>
          <label style={{ marginTop: 12 }}>{L("Propietario", "Owner")}</label>
          <input
            className="form-control"
            value={invoiceData.nombre_propietario}
            disabled
            style={{ opacity: 0.7 }}
          />
        </>
      )}

      <FilialDetailsSummary invoiceData={invoiceData} />
    </div>
  );
}

